import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LOCALES, isRTL, type Locale } from '@/lib/i18n';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/field';

export type LocalizedValue = Record<string, string>;

interface Props {
  /** One value per locale, keyed by code. Missing keys read as empty. */
  value: LocalizedValue;
  onChange: (next: LocalizedValue) => void;
  /** The locales this tenant publishes in; the first is the base. */
  locales: string[];
  multiline?: boolean;
  placeholder?: string;
  id?: string;
  autoFocus?: boolean;
}

/**
 * §25 — one field, every language, switched by the dots above it.
 *
 * A translation is a row keyed by locale on the same item, so the name a guest
 * reads is chosen by their `x-lang`, not by which record someone opened. That
 * only works if adding the Arabic name is as cheap as typing the English one —
 * a separate translations screen makes it a second trip nobody takes, and the
 * menu ships half-translated.
 *
 * The dot is filled when that locale has text, so a glance at the row says what
 * is still missing without opening each language in turn. The input flips its
 * own `dir` per locale: Arabic must be typed right-to-left even while the rest
 * of the dashboard is in English.
 */
export function LocalizedInput({
  value,
  onChange,
  locales,
  multiline,
  placeholder,
  id,
  autoFocus,
}: Props) {
  const { t } = useTranslation();
  const base = locales[0] ?? 'en';
  const [active, setActive] = useState(base);

  // A tenant can add a language while the dialog is open; never strand the
  // editor on a locale that is no longer offered.
  useEffect(() => {
    if (!locales.includes(active)) setActive(base);
  }, [active, base, locales]);

  const filled = useMemo(
    () => new Set(locales.filter((code) => (value[code] ?? '').trim().length > 0)),
    [locales, value],
  );

  const Control = multiline ? Textarea : Input;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1" role="tablist">
        {locales.map((code) => {
          const meta = LOCALES[code as Locale];
          const isActive = code === active;
          return (
            <button
              key={code}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActive(code)}
              title={meta?.nativeName ?? code}
              className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition ${
                isActive
                  ? 'bg-accent-solid text-accent-on'
                  : 'bg-[var(--muted)] text-ink-muted hover:text-[var(--foreground)]'
              }`}
            >
              <span
                aria-hidden
                className={`h-1.5 w-1.5 rounded-full ${
                  filled.has(code)
                    ? isActive
                      ? 'bg-white'
                      : 'bg-accent-solid'
                    : 'border border-current opacity-50'
                }`}
              />
              {code.toUpperCase()}
            </button>
          );
        })}
      </div>

      <Control
        id={id}
        autoFocus={autoFocus}
        dir={isRTL(active) ? 'rtl' : 'ltr'}
        lang={active}
        value={value[active] ?? ''}
        placeholder={placeholder}
        aria-label={LOCALES[active as Locale]?.nativeName ?? active}
        onChange={(event: { target: { value: string } }) =>
          onChange({ ...value, [active]: event.target.value })
        }
      />

      {/* Only the base language is required — the rest degrade to it at read
          time, so an untranslated item still has a name. */}
      {active !== base && !filled.has(active) && (
        <p className="text-xs text-ink-muted">
          {t('common.localized.fallsBack', {
            locale: LOCALES[base as Locale]?.nativeName ?? base,
          })}
        </p>
      )}
    </div>
  );
}
